/**
 * `mobile` namespace — English canonical source.
 *
 * Copy owned by the Expo app (`apps/mobile`): screen titles, tab labels, empty
 * states, buttons, hints, and native alert copy. Web-shared vocabulary stays in
 * its own namespaces (`common.*`, `nav.*`, …) — this namespace is only for
 * strings that exist on the phone. `ko/mobile.ts` mirrors this shape (a missing
 * key is a type error). Status/enum tokens (order states, run states) are NOT
 * translated — they render as mono badges, same as the web console.
 */
export const mobile = {
  appName: "richrich",

  // ── shared across screens ──
  errorCouldNotLoadTitle: "Couldn't load",
  loadingPortfolio: "Loading portfolio…",
  asOfLabel: "As of {date}",
  portfolioValueLabel: "Portfolio value · {currency}",
  statsStocks: "Stocks",
  statsCash: "Cash",
  portfolioNoSnapshotTitle: "No snapshot yet",
  portfolioNoSnapshotMessage: "Portfolio value appears after the first snapshot is taken.",
  dryRunLabel: "Dry run",
  liveLabel: "Live",
  updatedRelative: "Updated {when}",
  errorNotFound: "Not found",
  winRateLabel: "Win rate",
  payoffRatioLabel: "Payoff ratio",
  tradesLabel: "Trades",
  marketLabel: "Market",
  linkedLabel: "Linked",
  qtySuffixSh: "{qty} sh",

  // ── navigation: tab titles + stack titles ──
  tabDashboard: "Dashboard",
  tabStrategies: "Strategies",
  tabOrders: "Orders",
  tabApprovals: "Approvals",
  tabAlerts: "Alerts",
  settingsLabel: "Settings",
  builderChatLabel: "Builder chat",
  strategyDetailTitle: "Strategy",
  decisionReplayTitle: "Decision replay",
  backtestsLabel: "Backtests",
  backtestResultTitle: "Backtest result",
  portfolioTitle: "Portfolio",
  approvalTitle: "Approval",

  // ── login ──
  loginConnecting: "Connecting…",
  loginClaimHeading: "Claim this instance",
  loginSignInHeading: "Sign in to your instance",
  loginEmailLabel: "Email",
  loginEmailPlaceholder: "you@example.com",
  loginClaimHint: "The first email to verify becomes the sole owner.",
  loginCodeHint: "A one-time code will be emailed to the owner.",
  loginSending: "Sending…",
  loginSendCode: "Send code",
  loginCodeLabel: "Verification code",
  loginCodePlaceholder: "123456",
  loginCodeSentHint: "Sent to {email}. Codes expire in 10 minutes.",
  loginVerifying: "Verifying…",
  loginClaimContinue: "Claim & continue",
  loginSignIn: "Sign in",
  loginUseDifferentEmail: "Use a different email",
  loginQrTip:
    "Tip: scan the QR code in the web app's Settings with your camera to sign in without typing a code.",

  // ── QR sign-in ──
  qrHeading: "QR sign-in",
  qrMissingTokenError: "This QR link is missing its sign-in token.",
  qrErrorTitle: "Couldn't sign in with this QR",
  qrExpiredHint:
    "QR codes are single-use and expire after 2 minutes. Generate a fresh one from the web app's Settings and scan again.",
  qrBackToSignIn: "Back to sign in",
  qrSigningIn: "Signing in…",

  // ── dashboard ──
  dashboardConnectTossTitle: "Connect Toss to see your portfolio",
  dashboardConnectTossDesc:
    "Add and verify a Toss credential in Settings to unlock account value and holdings.",
  dashboardOpenSettings: "Open Settings",
  dashboardProfitVsDeposit30d: "Profit vs deposit · 30d",
  externalFlowLabel: "External flow",
  marketPnlLabel: "Market P&L",
  timeWeightedReturnLabel: "Time-weighted return",
  decompEndValueLabel: "End value",
  decompNetDepositsLabel: "Net deposits",
  decompTwrLabel: "TWR",
  decompCapitalLegend: "Capital (deposits)",
  decompGainLegend: "Market gain",
  decompLossLegend: "Market loss",
  decompValueLegend: "Value",

  // ── settings ──
  settingsLoading: "Loading settings…",
  settingsAccountHeader: "Account",
  settingsSignedInAs: "Signed in as",
  settingsSignOut: "Sign out",
  settingsPushHeader: "Push notifications",
  settingsPushFeatureLabel: "Push feature",
  gateUnlocked: "Unlocked",
  gateLocked: "Locked",
  settingsPushDesc:
    "Registering this device enables approval-required strategies. Requires EAS credentials: Android FCM V1 and iOS APNs + a paid Apple Developer account — these cannot be skipped.",
  settingsPushProjectIdWarning: "Set expo.extra.eas.projectId in app.json before registering.",
  settingsRegistering: "Registering…",
  settingsRegisterDevice: "Register this device",
  settingsPushRegistered: "Device registered for push notifications.",
  settingsTradingStateHeader: "Trading state",
  settingsGlobalTrading: "Global trading",
  tradingArmed: "Armed",
  tradingDisarmed: "Disarmed",
  settingsDefaultDryRun: "Default dry-run",
  onLabel: "On",
  offLabel: "Off",
  settingsBaseCurrency: "Base currency",
  settingsArmHint:
    "Arm live trading from the web app — it requires an explicit confirmation and a verified Toss credential.",
  settingsFeaturesHeader: "Features",
  settingsFeatureAi: "AI advisor / builder",
  settingsFeatureToss: "Toss trading",
  settingsFeaturePush: "Push",
  settingsAppHeader: "App",
  settingsApiOrigin: "API origin",

  // ── strategies (list) ──
  strategiesLoading: "Loading strategies…",
  strategyNextRun: "Next run {when}",
  strategiesEmptyTitle: "No strategies",
  strategiesEmptyAllMessage: "Create a strategy from the web app to see it here.",
  strategiesEmptyFilteredMessage: "No {filter} strategies.",

  // ── strategy detail ──
  strategyDetailLoading: "Loading strategy…",
  strategySlug: "Slug",
  strategySchema: "Schema",
  strategyAccount: "Account",
  notBound: "Not bound",
  strategyBudget: "Budget",
  nextRunLabel: "Next run",
  updatedLabel: "Updated",
  controlsHeader: "Controls",
  dryRunControlDesc: "Simulates fills — no live orders are placed while on.",
  pauseButton: "Pause",
  resumeButton: "Resume",
  pauseResumeUnavailable: "{status} strategies can't be paused or resumed from mobile.",
  strategyRulesHeader: "Strategy rules",
  performanceHeader: "Performance",
  realizedPnlLabel: "Realized P&L",
  recentRunsHeader: "Recent runs",
  noRunsYet: "No runs yet.",
  dryRunTag: "dry-run",

  // ── orders ──
  ordersFilterAll: "All",
  ordersFilterOpen: "Open",
  ordersFilterClosed: "Closed",
  ordersFilledLabel: "Filled {qty}",
  whyLabel: "Why?",
  cancellingLabel: "Cancelling…",
  cancelLabel: "Cancel",
  ordersLoading: "Loading orders…",
  cancelFailedTitle: "Cancel failed",
  cancelOrderConfirmTitle: "Cancel this order?",
  cancelDryRunNote: "(dry-run — simulated)",
  keepOrderLabel: "Keep order",
  cancelOrderLabel: "Cancel order",
  ordersEmptyTitle: "No orders",
  ordersEmptyAllMessage: "Orders appear here once a strategy submits one.",
  ordersEmptyOpenMessage: "No open orders.",
  ordersEmptyClosedMessage: "No closed orders.",

  // ── approvals (list) ──
  approvalsLoading: "Loading approvals…",
  approvalsEmptyTitle: "No pending approvals",
  approvalsEmptyMessage: "Approval-required strategies will surface order approvals here.",
  pendingLabel: "Pending",
  approvalsHighValueLabel: "High value",
  expiresLabel: "Expires {when}",

  // ── approval detail ──
  approvalDetailLoading: "Loading approval…",
  approvalNoLongerPendingTitle: "No longer pending",
  approvalNoLongerPendingMessage:
    "This approval has already been decided or has expired. The server-side timeout is authoritative.",
  approvalMissingTokenError: "Missing approval token.",
  qtySharesLabel: "{qty} shares",
  approvalHighValueOrder: "High value order",
  sideLabel: "Side",
  orderTypeLabel: "Order type",
  sizeLabel: "Size",
  priceLabel: "Price",
  currencyLabel: "Currency",
  timeInForceLabel: "Time in force",
  approvalWindowHeader: "Approval window",
  expiresFieldLabel: "Expires",
  countdownLabel: "Countdown",
  onTimeoutLabel: "On timeout",
  requestedLabel: "Requested",
  approveOrderButton: "Approve order",
  approvalTokenRequiredNote:
    "Approving requires the one-time token from the push notification. Open this approval from its push to approve; you can still reject it here.",
  rejectOrderButton: "Reject order",
  approvalDecidedLabel: "Decided {when}",
  approvalDecidedViaSuffix: " · via {via}",

  // ── backtests (config + list) ──
  backtestsGateTitle: "Backtests need market data",
  backtestsGateDesc:
    "Connect and verify a Toss credential in the web app's Settings to unlock historical candles for backtesting.",
  newBacktestHeader: "New backtest",
  fromLabel: "From",
  toLabel: "To",
  dateFormatPlaceholder: "YYYY-MM-DD",
  initialCashLabel: "Initial cash",
  queueingLabel: "Queueing…",
  runBacktestButton: "Run backtest",
  recentBacktestsHeader: "Recent backtests",
  backtestsEmptyTitle: "No backtests yet",
  backtestsEmptyMessage: "Run one with the form above.",

  // ── backtest result ──
  backtestDetailLoading: "Loading backtest…",
  backtestRunningNote: "Replaying against cached candles — this refreshes automatically.",
  equityCurveHeader: "Equity curve",
  startingCapitalLabel: "Starting capital",
  finalEquityLabel: "Final equity",
  strategyLegendLabel: "strategy",
  benchmarkLegendLabel: "benchmark",
  equityExcessLabel: "{value} vs benchmark",
  metricsHeader: "Metrics",
  totalReturnLabel: "Total return",
  cagrLabel: "CAGR",
  maxDrawdownLabel: "Max drawdown",
  benchmarkReturnLabel: "Benchmark return",
  benchmarkMddLabel: "Benchmark MDD",
  tradesCountValue: "{count} ({roundTrips} round trips)",
  backtestPriceRebasedNote:
    "Each line is rebased to 100 on its first day, so differently-priced assets compare on one % scale. RSI/ROC/drawdown and FGI read on the strip below.",
  backtestPriceIndicatorNote:
    "Indicators are computed from the shown window, so values near the start are approximate.",
  backtestPriceExpandLabel: "Expand chart",

  // ── builder chat: session list ──
  chatUnlockAiTitle: "Unlock the AI builder",
  chatUnlockAiDesc:
    "Add and verify an AI provider key in the web app's Settings to enable the strategy builder chat.",
  chatSessionsLoading: "Loading sessions…",
  deleteFailedTitle: "Delete failed",
  chatDeleteSessionTitle: "Delete this session?",
  chatDeleteMessage: '"{title}" and its message history will be removed.',
  untitledSessionLabel: "Untitled session",
  keepLabel: "Keep",
  deleteLabel: "Delete",
  creatingLabel: "Creating…",
  newSessionButton: "New session",
  chatSessionsEmptyTitle: "No sessions yet",
  chatSessionsEmptyMessage: "Start a session and describe the strategy you want to build.",

  // ── builder chat: conversation ──
  toolFailedLabel: "failed",
  chatConversationLoading: "Loading conversation…",
  chatDescribeStrategy: "Describe the strategy you want to build.",
  chatComposerPlaceholder: "Message the builder…",
  stopLabel: "Stop",
  sendLabel: "Send",
  jumpToLatestLabel: "Jump to latest",
  savingLabel: "Saving…",
  saveAsStrategyButton: "Save as strategy",
  draftStrategyLabel: "Draft strategy",
  turnRunningLabel: "A turn is still running server-side",
  strategySavedTitle: "Strategy saved",
  strategySavedMessage: '"{name}" was created as a draft strategy.',
  stayHereLabel: "Stay here",
  openStrategyLabel: "Open strategy",

  // ── notifications ──
  notificationsLoading: "Loading alerts…",
  markAllReadButton: "Mark all read",
  notificationsEmptyTitle: "No alerts",
  notificationsEmptyMessage: "Signals, fills, approvals, and risk events appear here.",

  // ── portfolio drill-down ──
  notEnoughSnapshotsMessage: "Not enough snapshots for this period yet.",
  allocationHeader: "Allocation",
  noHoldingsMessage: "No holdings yet.",
  cashSliceName: "Cash",
  allocationTotalLabel: "Total",
  cashflowsHeader: "Cashflows",
  cashflowsEmptyMessage: "Deposits, withdrawals, dividends, and fees appear here.",
  breakdownHeader: "Breakdown",

  // ── decision replay (run detail) ──
  indicatorsHeader: "Indicators",
  conditionsHeader: "Conditions",
  conditionTrueLabel: "true",
  conditionFalseLabel: "false",
  riskChecksHeader: "Risk checks",
  riskPassLabel: "pass",
  riskFailLabel: "fail",
  aiVerdictHeader: "AI verdict",
  sizeMultiplierLabel: "size ×{multiplier}",
  orderIntentHeader: "Order intent",
  typeLabel: "Type",
  marketLowercase: "market",
  runDetailLoading: "Loading decision replay…",
  noDecisionsMessage: "No decisions recorded for this run.",
  scheduledLabel: "Scheduled",
  startedLabel: "Started",
  finishedLabel: "Finished",
  tickLabel: "Tick",

  // ── market overview (dashboard card + Market screen) ──
  marketScreenTitle: "Market",
  marketSectionTitle: "Market",
  marketUnavailable: "Market data unavailable",
  marketLoadingLabel: "Loading market…",
  marketIndicesHeader: "Indices",
  marketFgiHeader: "Fear & Greed",
  marketSectorsHeader: "Sectors",
  marketHeatmapHeader: "Heatmap",
  marketNewsHeader: "News",
  marketIndicesEmpty: "No index data.",
  marketSectorsEmpty: "No sector data.",
  marketNewsEmpty: "No headlines.",
  // per-stock treemap (종목 히트맵) — the toggled "Stocks" view of the heatmap
  heatmapToggleSectors: "Sectors",
  heatmapToggleStocks: "Stocks",
  marketStocksEmpty: "No stock data.",
  marketLegendNoData: "No data",
  marketStocksAllSectors: "‹ All",
  marketStocksBaselinePending: "Change % appears after the next market close.",
  marketStocksLockedTitle: "The stock heatmap needs Toss",
  marketStocksLockedDesc:
    "Add and verify a Toss credential in Settings to unlock the per-stock heatmap.",
  marketFgiEmpty: "Fear & Greed unavailable.",
  marketFgiHistoryEmpty: "No history yet.",
  fgiScoreLabel: "Score",
  fgiHistoryLabel: "90-day trend",
  fgiExtremeFear: "Extreme Fear",
  fgiFear: "Fear",
  fgiNeutral: "Neutral",
  fgiGreed: "Greed",
  fgiExtremeGreed: "Extreme Greed",
  fgiPrevClose: "Prev close",
  fgiPrev1Week: "1 week ago",
  fgiPrev1Month: "1 month ago",
  fgiPrev1Year: "1 year ago",
  // index short labels (keyed by Yahoo symbol)
  idxGSPC: "S&P 500",
  idxIXIC: "Nasdaq",
  idxDJI: "Dow",
  idxRUT: "Russell",
  idxVIX: "VIX",
  // sector short labels (keyed by SPDR ETF symbol)
  secXLK: "Tech",
  secXLF: "Financials",
  secXLE: "Energy",
  secXLV: "Health",
  secXLI: "Industrials",
  secXLY: "Discretionary",
  secXLP: "Staples",
  secXLU: "Utilities",
  secXLB: "Materials",
  secXLRE: "Real Estate",
  secXLC: "Comms",
} as const;

export type MobileKey = keyof typeof mobile;
